import { useQuery, useMutation } from "convex/react";
import { api } from "../../api";
import { useToast } from "@/hooks/use-toast";
import { useBlueJayRulesRead } from "@/hooks/useBlueJayRulesRead";
import { useWebsiteCategories } from "@/hooks/useWebsiteCategories";
import type { BrowserMode } from "@/types/convex";

/**
 * Hook to manage device browser mode (none, categories, whitelist).
 * 
 * Key principle: Mode changes ONLY update the browserMode field - they never modify
 * blueJayRules categories, allowlist, or blocklist. This allows settings to persist
 * when switching between modes.
 * 
 * Note: browserMode is stored in deviceConfig, NOT in blueJayRules.
 * 
 * Priority chain:
 * 1. Device browserMode (if set, used directly)
 * 2. Level browserMode (if device doesn't override, inherit from level)
 * 3. Legacy inference (if neither set, inferred from blueJayRules for backwards compatibility)
 * 
 * Sidebar visibility by mode:
 * - none: Settings only
 * - whitelist: Settings, Whitelist only
 * - categories: All sections (Settings, Categories, Whitelist, Blacklist)
 */
export function useDeviceBrowserMode(deviceId: string) {
  const { toast } = useToast();
  
  // Get device details to access deviceConfig ID
  const deviceData = useQuery(
    api.devices.get.details, 
    deviceId ? { device: deviceId as any } : "skip"
  );
  
  // Get device policies to read browserMode
  const devicePolicies = useQuery(
    api.devices.policies.get,
    deviceData?.device?.deviceConfig ? { deviceConfig: deviceData.device.deviceConfig } : "skip"
  );
  
  // Read blueJayRules from external endpoint for fallback mode detection
  const { blueJayRules, isLoading: isLoadingRules, refetch } = useBlueJayRulesRead(deviceId);
  
  // Get all website categories for whitelist mode detection (legacy fallback)
  const { categories: allCategories } = useWebsiteCategories();
  
  // Mutation for updating device policies - sends browserMode directly
  const updateDevicePolicies = useMutation(api.devices.policies.update);
  
  /**
   * Determine current browser mode.
   * Priority: 
   * 1. Explicit browserMode from deviceConfig (where backend stores it)
   * 2. Inferred from blueJayRules structure (fallback for backwards compatibility)
   */
  const getBrowserMode = (): BrowserMode => {
    // Read browserMode from devicePolicies
    const explicitMode = devicePolicies?.browserMode;
    if (explicitMode && ["none", "categories", "whitelist"].includes(explicitMode)) {
      return explicitMode as BrowserMode;
    }
    
    // Fallback: infer from blueJayRules for backwards compatibility
    
    // blueJayRules = false means browser disabled
    if (blueJayRules === false) {
      return "none";
    }
    
    // Whitelist mode = ONLY when ALL categories are blocked
    const totalCategoryCount = allCategories.length;
    const blockedCategoryCount = blueJayRules?.categories?.length ?? 0;
    
    if (totalCategoryCount > 0 && blockedCategoryCount === totalCategoryCount) {
      return "whitelist";
    }
    
    // Any other case is filtered/categories mode
    return "categories";
  };
  
  const browserMode = getBrowserMode();
  
  /**
   * Set the browser mode.
   * 
   * Sends browserMode directly to backend - does NOT modify blueJayRules categories,
   * allowlist, or blocklist. This allows settings to persist when switching between modes.
   */
  const setBrowserMode = async (mode: BrowserMode) => {
    if (!deviceId) {
      toast({
        title: "Error",
        description: "Device ID not available",
        variant: "destructive",
      });
      return;
    }
    
    try {
      // Send browserMode directly to backend
      // Cast to any since api.ts types may not include browserMode yet
      await updateDevicePolicies({
        device: deviceId,
        browserMode: mode,
      } as any);
      
      // Refetch to update state
      refetch();
      
      toast({
        title: "Browser Mode Updated",
        description: `Device browser mode set to ${mode}.`,
      });
    } catch (error) {
      console.error("Error updating browser mode:", error);
      toast({
        title: "Error",
        description: "Failed to update browser mode",
        variant: "destructive",
      });
    }
  };
  
  return {
    browserMode,
    setBrowserMode,
    isLoading: deviceData === undefined || devicePolicies === undefined || isLoadingRules,
    refetch,
  };
}
